import { ZodSchema } from 'zod'
import { t } from 'i18next'
import { FormikErrors } from 'formik'
import { DateTime } from 'luxon'

import { validationErrors } from '../../common/validations'
import { formValidationErrorMapping } from '../validations'

export const localisedErrorMessage = (error: string) => {
  switch (error) {
    case validationErrors.required:
      return t('errors:validation.required')
    case validationErrors.invalid_date:
      return t('errors:validation.invalidDate')
    case validationErrors.too_small_date:
      return t('errors:validation.tooSmallDate')
    case validationErrors.too_big_date:
      return t('errors:validation.tooBigDate')
    case validationErrors.invalid_postal_code:
      return t('errors:validation.invalidPostalCode')
    case validationErrors.invalid_number:
      return t('errors:validation.invalidNumber')
    case validationErrors.too_big:
      return t('errors:validation.tooBig')
    case validationErrors.too_small:
      return t('errors:validation.tooSmall')
    case validationErrors.invalid_iban:
      return t('errors:validation.invalidIban')
    case validationErrors.invalid_bic:
      return t('errors:validation.invalidBic')
    case validationErrors.invalid_type:
      return t('errors:validation.invalidType')
    case validationErrors.invalid_email:
      return t('errors:validation.invalidEmail')
    case validationErrors.invalid_phone_number:
      return t('errors:validation.invalidPhoneNumber')
    case validationErrors.invalid_string:
      return t('errors:validation.invalidString')
    case validationErrors.invalid_ssn:
      return t('errors:validation.invalidSsn')
    case validationErrors.invalid_pin:
      return t('errors:validation.invalidPin')
    case validationErrors.move_date_after_end_date:
      return t('errors:validation.moveDateAfterEndDate')
    case validationErrors.invalid_return_percentage_sum:
      return t('errors:validation.invalidReturnPercentageSum')
    case validationErrors.invalid_roo_return_percentage_sum:
      return t('errors:validation.invalidRooReturnPercentageSum')
    case validationErrors.title_description_or_files_required:
      return t('errors:validation.titleDescriptionOrFilesRequired')
    case validationErrors.no_resident_user:
      return t('errors:validation.noResidentUser')
    case validationErrors.terminated_adult_missing_resident_user:
      return t('errors:validation.terminatedAdultMissingResidentUser')
    case validationErrors.no_adult_left:
      return t('errors:validation.noAdultLeft')
    case validationErrors.other_contract_party_termination:
      return t('errors:validation.otherContractPartyTermination')
    default:
      return error
  }
}

/**
 * Validate formik form values using a Zod schema
 * @param values - The formik form values to validate
 * @param schema - The Zod schema to use for validation
 * @returns An object with validation errors
 */
export const validateFormValues = <T>(
  values: unknown,
  schema: ZodSchema<T>
): FormikErrors<T> => {
  const validation = schema.safeParse(values, {
    errorMap: formValidationErrorMapping,
  })

  // Map errors to a Formik like error object
  return validation.success
    ? {}
    : (validation.error.errors.reduce(
        (errors, error) =>
          error.path.reduce((a, b, level) => {
            if (level === error.path.length - 1) {
              a[b] = localisedErrorMessage(error.message)
              return errors
            }
            if (!a[b]) {
              a[b] = {}
            }
            return a[b]
          }, errors),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {} as { [key: string]: any }
      ) as FormikErrors<T>)
}

/**
 * Convert a locale date string to an ISO date string
 * @param {string} dateString - The date string in locale format (dd.MM.yyyy)
 * @returns {string} The date string in ISO format
 */
export const ISODateFromLocaleDate = (dateString: string) =>
  DateTime.fromFormat(dateString, 'dd.MM.yyyy').toISODate()

/**
 * Convert a locale date string to an ISO date string
 * @param {string} dateString - The date string in locale format (dd.MM.yyyy)
 * @returns {string} The date string in ISO format
 */
export const safeISODateFromLocaleDate = (dateString: string | null) => {
  const dateTime = DateTime.fromFormat(dateString ?? '', 'dd.MM.yyyy')
  return dateTime.isValid ? dateTime.toISODate() : null
}

/**
 * Render a date string in locale format
 * @param {string} date - The date string in ISO format or locale format
 * @returns {string} The date string in locale format (dd.MM.yyyy)
 */
export const renderDate = (date: string): string => {
  return DateTime.fromISO(date).isValid ? LocalDateFromISODate(date) : date
}

/**
 * Convert ISO date to a locale date string
 * @param {string} dateString - The date string in ISO format
 * @returns {string} The date string in locale format (dd.MM.yyyy)
 */
export const LocalDateFromISODate = (dateString: string) =>
  DateTime.fromISO(dateString).toFormat('dd.MM.yyyy')

export const sleep = (ms: number) => {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms)
  })
}

/**
 * Helper function to return a value or '-' if the value is null or undefined
 * @param {string | number | null | undefined} value - The value to check
 * @returns {string | number} The value or '-' if the value is null or undefined
 */
export const valueOrEmpty = (
  value?: string | number | null | undefined
): string | number => {
  return value || value === 0 ? value : '-'
}
